import React, { useState, useEffect } from 'react';
import { Menu, X, LogOut } from 'lucide-react';
import { Button } from './ui/button';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';

const LOGO_WHITE = "https://customer-assets.emergentagent.com/job_a85f478b-763e-4b7e-9f54-0dbb5a37a168/artifacts/fk4lgbat_Logo%20Phocea%20Rent.png";
const API = `${window.location.origin}/api`;

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('phocea_session_token');
    setIsLoggedIn(!!token);
  }, [location]);

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (e) {}
    localStorage.removeItem('phocea_session_token');
    setIsLoggedIn(false);
    navigate('/');
  };

  const scrollToSection = (id) => {
    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    } else {
      const element = document.getElementById(id);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
    setIsMobileMenuOpen(false);
  };

  const goToReservation = () => {
    navigate('/reservation');
    setIsMobileMenuOpen(false);
  };

  return (
    <header
      className={`fixed top-[32px] left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-black/95 backdrop-blur-md shadow-lg'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <a href="/" className="flex items-center group">
            <img 
              src={LOGO_WHITE} 
              alt="Phocea Rent" 
              className="h-12 w-auto transform group-hover:scale-105 transition-transform"
            />
          </a>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8">
            {[
              { label: 'Nos Packs', id: 'packs' },
              { label: 'Comment ça marche', id: 'process' },
              { label: 'Avantages', id: 'advantages' },
              { label: 'FAQ', id: 'faq' },
            ].map((item) => (
              <button
                key={item.id}
                onClick={() => scrollToSection(item.id)}
                className="text-sm font-medium transition-colors text-white/80 hover:text-[#DFFF00]"
              >
                {item.label}
              </button>
            ))}
            <button
              onClick={() => navigate('/contact')}
              className="text-sm font-medium transition-colors text-white/80 hover:text-[#DFFF00]"
            >
              Contact
            </button>
            <button
              onClick={() => navigate('/light')}
              className="text-sm font-medium transition-colors text-white/80 hover:text-[#DFFF00]"
              data-testid="nav-catalogue-light"
            >
              Catalogue Light
            </button>
          </nav>

          {/* CTA Button */}
          <div className="hidden md:flex items-center gap-4">
            {isLoggedIn ? (
              <>
                <button
                  onClick={() => navigate('/dashboard')}
                  className="text-sm font-medium text-[#DFFF00] hover:text-[#c8e600] transition-colors flex items-center gap-1.5"
                  data-testid="header-my-space-btn"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                  Mon espace
                </button>
                <button
                  onClick={handleLogout}
                  className="text-sm text-slate-400 hover:text-red-400 transition-colors"
                  data-testid="header-logout-btn"
                  title="Déconnexion"
                >
                  <LogOut className="w-4 h-4" />
                </button>
              </>
            ) : (
              <button
                onClick={() => navigate('/login')}
                className="text-sm font-medium text-white/80 hover:text-[#DFFF00] transition-colors"
              >
                Connexion
              </button>
            )}
            <Button
              onClick={goToReservation}
              className="bg-[#DFFF00] hover:bg-[#c8e600] text-black font-semibold px-6 py-2 rounded-full shadow-lg hover:shadow-xl transition-all"
            >
              Réserver maintenant
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden p-2"
          >
            {isMobileMenuOpen ? (
              <X className="w-6 h-6 text-white" />
            ) : (
              <Menu className="w-6 h-6 text-white" />
            )}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden bg-black/95 backdrop-blur-md rounded-2xl shadow-xl mt-2 p-6 animate-in slide-in-from-top-2 border border-[#DFFF00]/20">
            <nav className="flex flex-col gap-4">
              {[
                { label: 'Nos Packs', id: 'packs' },
                { label: 'Comment ça marche', id: 'process' },
                { label: 'Avantages', id: 'advantages' },
                { label: 'FAQ', id: 'faq' },
              ].map((item) => (
                <button
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  className="text-left text-white font-medium py-2 hover:text-[#DFFF00] transition-colors"
                >
                  {item.label}
                </button>
              ))}
              <button
                onClick={() => {
                  navigate('/contact');
                  setIsMobileMenuOpen(false);
                }}
                className="text-left text-white font-medium py-2 hover:text-[#DFFF00] transition-colors"
              >
                Contact
              </button>
              <button
                onClick={() => {
                  navigate('/light');
                  setIsMobileMenuOpen(false);
                }}
                className="text-left text-white font-medium py-2 hover:text-[#DFFF00] transition-colors"
                data-testid="nav-catalogue-light-mobile"
              >
                Catalogue Light
              </button>
              <Button
                onClick={goToReservation}
                className="bg-[#DFFF00] hover:bg-[#c8e600] text-black font-semibold mt-4 rounded-full"
              >
                Réserver maintenant
              </Button>
              {isLoggedIn ? (
                <>
                  <button
                    onClick={() => {
                      navigate('/dashboard');
                      setIsMobileMenuOpen(false);
                    }}
                    className="text-[#DFFF00] hover:text-[#c8e600] text-center py-2 font-medium"
                  >
                    Mon espace
                  </button>
                  <button
                    onClick={() => {
                      handleLogout();
                      setIsMobileMenuOpen(false);
                    }}
                    className="text-slate-400 hover:text-red-400 text-center py-2 flex items-center justify-center gap-2"
                  >
                    <LogOut className="w-4 h-4" /> Déconnexion
                  </button>
                </>
              ) : (
                <button
                  onClick={() => {
                    navigate('/login');
                    setIsMobileMenuOpen(false);
                  }}
                  className="text-white/80 hover:text-[#DFFF00] text-center py-2"
                >
                  Connexion
                </button>
              )}
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
